<template>
  <div
    v-if="report && report.configuration"
    class="report-config-form grow d-flex flex-column"
  >
    <div class="grow d-flex align-stretch flex-nowrap pb-2">
      <v-col class="form--left-col d-flex pt-6">
        <v-row class="flex-column" justify="start">
          <div class="primary-header text-center mb-2">
            {{ $t('modal.report.config.general.optionalPages.title') }}
          </div>
          <v-col class="shrink">
            <platform-switch
              hide-details
              compact-label
              v-model="showDiversity"
              v-tooltip="showDiversity ? $t('modal.report.config.general.optionalPages.diversityTooltipOn') : $t('modal.report.config.general.optionalPages.diversityTooltipOff')"
              style="max-width: 225px;"
              class="mx-auto"
              :label="$t('modal.report.config.general.optionalPages.diversityLabel')"
            />
          </v-col>
          <v-col class="shrink">
            <platform-switch
              hide-details
              compact-label
              v-model="showTenure"
              v-tooltip="showTenure ? $t('modal.report.config.general.optionalPages.tenureTooltipOn') : $t('modal.report.config.general.optionalPages.tenureTooltipOff')"
              style="max-width: 225px;"
              class="mx-auto"
              :label="$t('modal.report.config.general.optionalPages.tenureLabel')"
            />
          </v-col>
          <v-col class="shrink">
            <platform-switch
              hide-details
              compact-label
              v-model="showKnowhowAreas"
              v-tooltip="showTenure ? $t('modal.report.config.general.optionalPages.knowHowAreasTooltipOn') : $t('modal.report.config.general.optionalPages.knowHowAreasTooltipOff')"
              style="max-width: 225px;"
              class="mx-auto"
              :label="$t('modal.report.config.general.optionalPages.knowHowAreasLabel')"
            />
          </v-col>
          <v-col class="shrink">
            <platform-switch
              hide-details
              compact-label
              v-model="showRoles"
              v-tooltip="showTenure ? $t('modal.report.config.general.optionalPages.rolesTooltipOn') : $t('modal.report.config.general.optionalPages.rolesTooltipOff')"
              style="max-width: 225px;"
              class="mx-auto"
              :label="$t('modal.report.config.general.optionalPages.rolesLabel')"
            />
          </v-col>
          <v-col class="shrink">
            <platform-switch
              hide-details
              compact-label
              v-model="showEvaluationInformation"
              v-tooltip="showEvaluationInformation ? $t('modal.report.config.general.optionalPages.evaluationInformationTooltipOn') : $t('modal.report.config.general.optionalPages.evaluationInformationTooltipOff')"
              style="max-width: 225px;"
              class="mx-auto"
              :label="$t('modal.report.config.general.optionalPages.evaluationInformationLabel')"
            />
          </v-col>
          <div v-if="showBBOverallEffectivenessOption || showUniverseOverallEffectivenessOption">
            <div class="primary-header text-center mt-4 mb-2">
              {{ $t('modal.report.config.general.optionalPages.overallEffectiveness.title') }}
            </div>
            <v-col v-if="showBBOverallEffectivenessOption" class="shrink">
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <div v-bind="attrs" v-on="on">
                    <platform-switch
                      hide-details
                      compact-label
                      v-model="showBetterBoards"
                      :disabled="!systemBenchmarkMarkEnabled"
                      :label="$t('modal.report.config.general.optionalPages.overallEffectiveness.betterBoardsLabel', [systemBenchmark?.title ?? resultSet?.company.name])"
                      style="max-width: 200px;"
                      class="mx-auto"
                    />
                  </div>
                </template>
                <span>{{ betterBoardsTooltip }}</span>
              </v-tooltip>
            </v-col>
            <v-col v-if="showUniverseOverallEffectivenessOption" class="shrink">
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <div v-bind="attrs" v-on="on">
                    <platform-switch
                      hide-details
                      compact-label
                      v-model="showUniverse"
                      :disabled="!universeBenchmarkMarkEnabled"
                      style="max-width: 200px;"
                      class="mx-auto"
                      :label="$t('modal.report.config.general.optionalPages.overallEffectiveness.universeLabel')"
                    />
                  </div>
                </template>
                <span>{{ universeTooltip }}</span>
              </v-tooltip>
            </v-col>
          </div>
        </v-row>
      </v-col>
      <v-col class="form--right-col d-flex pt-6">
        <v-row class="flex-column" justify="start">
          <div class="primary-header text-center mb-2">
            {{ $t('modal.report.config.general.outputConfigTitle') }}
          </div>
          <v-col class="shrink">
            <platform-text-field
              hide-details
              v-model="sectionAverageTitle"
              v-tooltip="$t('modal.report.config.general.sectionAverageTitle.tooltip')"
              style="max-width: 250px;"
              class="mx-auto"
              :label="$t('modal.report.config.general.sectionAverageTitle.label')"
            />
          </v-col>
          <v-col class="shrink">
            <platform-select-field
              hide-details
              v-model="pageSize"
              v-tooltip="$t('modal.report.config.pageSize.tooltip')"
              style="max-width: 250px;"
              class="mx-auto"
              :items="availableReportPageSizes"
              :label="$t('modal.report.config.pageSize.label')"
            />
          </v-col>
        </v-row>
      </v-col>
    </div>
  </div>
</template>

<script lang="ts">
import { SelectItem } from '@/helpers/forms'
import { PlatformReportGroup, ReportConfiguration, ReportPageSize } from '@betterboards/shared/types/Report'
import Vue, { PropType } from 'vue'
import { PlatformReport } from '@/types/Report'
import { ResultSet } from '@betterboards/shared/types/ResultSet'
import {
  showBBOverallEffectivenessOption,
  showUniverseOverallEffectivenessOption
} from '@betterboards/shared/helpers/entities/Report/showOverallEffectiveness'
import { ReportBenchmark } from '@betterboards/shared/types/ReportBenchmark'
import { ScoreCollection } from '@betterboards/shared/helpers/entities/Result/scores'
import { PlatformSurveyGroup } from '@betterboards/shared/types/SurveyGroup'
import { ReportQuartiles } from '@betterboards/shared/types/Quartiles'
import { PlatformCompany } from '@betterboards/shared/types/Company'

export default Vue.extend({
  name: 'ReportConfigForm',
  components: {
    //
  },
  props: {
    value: { type: Object as PropType<PlatformReport>, required: false }
  },
  data: () => ({
    //
  }),
  computed: {
    resultSet (): ResultSet | null {
      return this.$store.state.Analysis.resultSet
    },
    company (): PlatformCompany {
      return this.$store.state.Company.selectedCompany
    },
    availableReportPageSizes (): SelectItem[] {
      return Object.values(ReportPageSize).map((pageSize: string): SelectItem => ({
        value: pageSize,
        text: this.$t(`modal.report.config.pageSize.${pageSize}`) as string
      }))
    },
    reportGroup (): PlatformReportGroup {
      return this.$store.getters['Report/selectedReportGroup']
    },
    surveyGroup (): PlatformSurveyGroup | undefined {
      return this.$store.getters['Analysis/selectedSurveyGroup']
    },
    report: {
      set (val: PlatformReport): void {
        this.$emit('input', val)
      },
      get (): PlatformReport {
        return this.value
      }
    },
    showDiversity: {
      set (showDiversity: boolean): void {
        this.updateReportConfiguration({
          showDiversity
        })
      },
      get (): boolean {
        return !!this.report.configuration?.showDiversity
      }
    },
    showTenure: {
      set (showTenure: boolean): void {
        this.updateReportConfiguration({
          showTenure
        })
      },
      get (): boolean {
        return !!this.report.configuration?.showTenure
      }
    },
    showKnowhowAreas: {
      set (knowhowAreas: boolean): void {
        this.updateReportConfiguration({
          appendix: {
            ...this.report.configuration?.appendix,
            knowhowAreas
          }
        })
      },
      get (): boolean {
        return !!this.report.configuration?.appendix?.knowhowAreas
      }
    },
    showRoles: {
      set (roles: boolean): void {
        this.updateReportConfiguration({
          appendix: {
            ...this.report.configuration?.appendix,
            roles
          }
        })
      },
      get (): boolean {
        return !!this.report.configuration?.appendix?.roles
      }
    },
    showEvaluationInformation: {
      set (show: boolean): void {
        this.updateReportConfiguration({
          pages: {
            ...this.report.configuration?.pages,
            evaluationInformation: {
              ...this.report.configuration?.pages?.evaluationInformation,
              show
            }
          }
        })
      },
      get (): boolean {
        return this.report.configuration?.pages?.evaluationInformation?.show !== false
      }
    },
    pageSize: {
      set (pageSize: ReportPageSize): void {
        this.updateReportConfiguration({
          pageSize
        })
      },
      get (): ReportPageSize {
        if (!this.report.configuration?.pageSize) {
          return ReportPageSize.A4
        }
        return this.report.configuration?.pageSize
      }
    },
    sectionAverageTitle: {
      set (sectionAverageTitle: string): void {
        this.updateReportConfiguration({
          sectionAverageTitle
        })
      },
      get (): string {
        if (!this.report.configuration?.sectionAverageTitle) {
          return this.company.name
        }
        return this.report.configuration.sectionAverageTitle
      }
    },
    showBetterBoards: {
      set (showBetterBoards: boolean): void {
        this.updateReportConfiguration({
          pages: {
            ...this.report.configuration?.pages,
            overallEffectiveness: {
              ...this.report.configuration?.pages?.overallEffectiveness,
              showBetterBoards
            }
          }
        })
      },
      get (): boolean {
        return !!this.report.configuration?.pages?.overallEffectiveness?.showBetterBoards
      }
    },
    showUniverse: {
      set (showUniverse: boolean): void {
        this.updateReportConfiguration({
          pages: {
            ...this.report.configuration?.pages,
            overallEffectiveness: {
              ...this.report.configuration?.pages?.overallEffectiveness,
              showUniverse
            }
          }
        })
      },
      get (): boolean {
        return !!this.report.configuration?.pages?.overallEffectiveness?.showUniverse
      }
    },
    betterBoardsTooltip (): string {
      const title = this.systemBenchmark?.title ?? this.resultSet?.company.name
      if (!this.systemBenchmarkMarkEnabled) {
        return this.$t('modal.report.config.general.optionalPages.overallEffectiveness.tooltipDisabled', [title]) as string
      }
      return this.showBetterBoards
        ? this.$t('modal.report.config.general.optionalPages.overallEffectiveness.betterBoardsTooltipOn', [title]) as string
        : this.$t('modal.report.config.general.optionalPages.overallEffectiveness.betterBoardsTooltipOff', [title]) as string
    },
    universeTooltip (): string {
      if (!this.universeBenchmarkMarkEnabled) {
        return this.$t('modal.report.config.general.optionalPages.overallEffectiveness.tooltipDisabled') as string
      }
      return this.showUniverse
        ? this.$t('modal.report.config.general.optionalPages.overallEffectiveness.universeTooltipOn') as string
        : this.$t('modal.report.config.general.optionalPages.overallEffectiveness.universeTooltipOff') as string
    },
    showBBOverallEffectivenessOption (): boolean {
      return showBBOverallEffectivenessOption(this.surveyGroup?.featureConfiguration?.overallEffectiveness) && this.validSystemQuartile
    },
    systemBenchmark (): ReportBenchmark | undefined {
      return this.reportGroup?.scores?.find(
        (score: ReportBenchmark) => score.collection === ScoreCollection.SystemAverage
      ) as ReportBenchmark
    },
    systemBenchmarkMarkEnabled (): boolean {
      return !!this.systemBenchmark?.enabled
    },
    showUniverseOverallEffectivenessOption (): boolean {
      return showUniverseOverallEffectivenessOption(this.surveyGroup?.featureConfiguration?.overallEffectiveness) && this.validUniverseQuartile
    },
    universeBenchmark (): ReportBenchmark | undefined {
      return this.reportGroup?.scores?.find(
        (score: ReportBenchmark) => score.collection === ScoreCollection.UniverseAverage
      ) as ReportBenchmark
    },
    universeBenchmarkMarkEnabled (): boolean {
      return !!this.universeBenchmark?.enabled && this.validUniverseQuartile
    },
    validSystemQuartile (): boolean {
      const quartiles: ReportQuartiles | undefined = this.reportGroup?.quartiles?.find(
        (quartile: ReportQuartiles) => quartile.collection === ScoreCollection.SystemAverage
      )
      const validBands: boolean | undefined = quartiles?.quartiles?.quartileBands?.every((band) => band.lower && band.upper)

      return !!validBands
    },
    validUniverseQuartile (): boolean {
      const quartiles: ReportQuartiles | undefined = this.reportGroup?.quartiles?.find(
        (quartile: ReportQuartiles) => quartile.collection === ScoreCollection.UniverseAverage
      )
      const validBands: boolean | undefined = quartiles?.quartiles?.quartileBands?.every((band) => band.lower && band.upper)

      return !!validBands
    }
  },
  methods: {
    updateReport (reportData: Partial<PlatformReport>): void {
      this.report = {
        ...this.report,
        ...reportData
      }
    },
    updateReportConfiguration (configData: Partial<ReportConfiguration>): void {
      this.updateReport({
        configuration: {
          ...this.report.configuration,
          ...configData
        }
      })
    }
  }
})
</script>

<style lang="scss" scoped="scoped">
.report-config-form {
  min-height: 250px;

  $col-padding: 30px;
  $border-width: 2px;
  .form--left-col {
    border-right: $border-width solid var(--v-grey-lighten3);
    padding-right: #{$col-padding - $border-width};
  }
  .form--right-col {
    padding-left: $col-padding;
  }
}
</style>
